/**
 * Jotai atoms for orders feature state management
 * Manages filter, sort, pagination, and selection state
 */
import { atom } from 'jotai';

import { OrdersServiceSort } from '@ekhodealer/ekho-common';

import type { OrdersTableTab } from '@ekhodealer/ekho-common';
import type { SortDirection } from '@ekhodealer/ekho-common/rev';
import type { Selection } from 'react-aria-components';
import type { SearchOrdersParams } from '../api/useSearchOrders';
import type { FulfillmentType } from '../components/filters/FiltersDrawerFulfillmentSection';

import atomWithDebounce from '@common/atoms/atomWithDebounce';
import { getCommonDateRange } from '@common/lib/filters';
import { getEndOfDay } from '@common/lib/time';
import { CommonDateFilter } from '@common/types/filters';

import { OrdersTableColumnId } from '../constants/ordersTableColumns';
import { DeliveryDateFilter } from '../types/filters';
import { getDeliveryDateRange } from '../utils/filters';

/**
 * Maps column ID and sort direction to OrdersServiceSort enum value
 * Used to convert UI sort state to API sort parameter
 */
function mapColumnToOrdersServiceSort(
	columnId: string,
	direction: SortDirection,
): OrdersServiceSort | undefined {
	if (direction === 'none') {
		return undefined;
	}

	const sortMap: Record<string, { asc: OrdersServiceSort; desc: OrdersServiceSort }> = {
		// Order # column sorts by order date since order numbers are assigned chronologically
		[OrdersTableColumnId.ORDER_ID]: {
			asc: OrdersServiceSort.ORDER_DATE_ASC,
			desc: OrdersServiceSort.ORDER_DATE_DESC,
		},
		[OrdersTableColumnId.BUYER]: {
			asc: OrdersServiceSort.BUYER_ASC,
			desc: OrdersServiceSort.BUYER_DESC,
		},
		[OrdersTableColumnId.ORDERS_TABLE_TAB]: {
			asc: OrdersServiceSort.ORDERS_TABLE_TAB_ASC,
			desc: OrdersServiceSort.ORDERS_TABLE_TAB_DESC,
		},
		[OrdersTableColumnId.ORDER_DATE]: {
			asc: OrdersServiceSort.ORDER_DATE_ASC,
			desc: OrdersServiceSort.ORDER_DATE_DESC,
		},
		[OrdersTableColumnId.VEHICLE]: {
			asc: OrdersServiceSort.VEHICLE_ASC,
			desc: OrdersServiceSort.VEHICLE_DESC,
		},
		[OrdersTableColumnId.PGA]: {
			asc: OrdersServiceSort.PGA_ASC,
			desc: OrdersServiceSort.PGA_DESC,
		},
		[OrdersTableColumnId.FI]: {
			asc: OrdersServiceSort.FI_ASC,
			desc: OrdersServiceSort.FI_DESC,
		},
		[OrdersTableColumnId.FI_PROFIT]: {
			asc: OrdersServiceSort.FI_PRU_ASC,
			desc: OrdersServiceSort.FI_PRU_DESC,
		},
		[OrdersTableColumnId.TOTAL]: {
			asc: OrdersServiceSort.TOTAL_ASC,
			desc: OrdersServiceSort.TOTAL_DESC,
		},
		[OrdersTableColumnId.PAYMENT_CHOICE]: {
			asc: OrdersServiceSort.PAYMENT_CHOICE_ASC,
			desc: OrdersServiceSort.PAYMENT_CHOICE_DESC,
		},
		[OrdersTableColumnId.PAYMENT]: {
			asc: OrdersServiceSort.PAYMENT_STATUS_ASC,
			desc: OrdersServiceSort.PAYMENT_STATUS_DESC,
		},
		[OrdersTableColumnId.FINANCING]: {
			asc: OrdersServiceSort.FINANCING_STATUS_ASC,
			desc: OrdersServiceSort.FINANCING_STATUS_DESC,
		},
		[OrdersTableColumnId.REGISTRATION_STATE]: {
			asc: OrdersServiceSort.REG_STATE_ASC,
			desc: OrdersServiceSort.REG_STATE_DESC,
		},
		[OrdersTableColumnId.VIN]: {
			asc: OrdersServiceSort.VIN_ASC,
			desc: OrdersServiceSort.VIN_DESC,
		},
		[OrdersTableColumnId.FULFILLMENT_TYPE]: {
			asc: OrdersServiceSort.FULFILLMENT_TYPE_ASC,
			desc: OrdersServiceSort.FULFILLMENT_TYPE_DESC,
		},
		[OrdersTableColumnId.EST_FULFILLMENT_DATE]: {
			asc: OrdersServiceSort.FULFILLMENT_DATE_ASC,
			desc: OrdersServiceSort.FULFILLMENT_DATE_DESC,
		},
		[OrdersTableColumnId.TASKS]: {
			asc: OrdersServiceSort.TASKS_ASC,
			desc: OrdersServiceSort.TASKS_DESC,
		},
	};

	const columnSort = sortMap[columnId];
	if (!columnSort) {
		return undefined;
	}

	return direction === 'ascending' ? columnSort.asc : columnSort.desc;
}

// Search value atoms with debouncing
const searchDebounceAtoms = atomWithDebounce('', 500);
export const debouncedSearchValueAtom = searchDebounceAtoms.debouncedValueAtom;
export const searchValueAtom = searchDebounceAtoms.currentValueAtom;

// Orders table tab filter atom
export const orderTabFilterAtom = atom<OrdersTableTab | null>(null);

// Action required filter atom
export const actionRequiredFilterAtom = atom<boolean>(false);

// Payment choice filter atoms (multiple selections)
export const paymentChoiceFilterAtom = atom<string[]>([]);

// Payment status filter atoms (multiple selections)
export const paymentStatusFilterAtom = atom<string[]>([]);

// Financing status filter atoms (multiple selections)
export const financingStatusFilterAtom = atom<string[]>([]);

// Model filter atoms (multiple selections)
export const modelFilterAtom = atom<string[]>([]);

// Type filter atoms (multiple selections)
export const typeFilterAtom = atom<string[]>([]);

// Registration state filter atom (single selection)
export const registrationStateFilterAtom = atom<string | null>(null);

// Order date filter atoms (single selection from predefined ranges)
export const orderDateFilterAtom = atom<CommonDateFilter | null>(null);

// Custom order date range (Unix ms timestamps, used when orderDateFilterAtom === CUSTOM)
export const orderDateCustomRangeAtom = atom<{ from: number | null; to: number | null }>({
	from: null,
	to: null,
});

// Fulfillment type filter atom (delivery or pickup)
export const fulfillmentTypeFilterAtom = atom<FulfillmentType | null>(null);

// Fulfillment date filter atom (shared for delivery and pickup)
export const fulfillmentDateFilterAtom = atom<DeliveryDateFilter | null>(null);

// Custom fulfillment date range (Unix ms timestamps, used when fulfillmentDateFilterAtom === CUSTOM)
export const fulfillmentDateCustomRangeAtom = atom<{ from: number | null; to: number | null }>({
	from: null,
	to: null,
});

// Sorting configuration
export const sortConfigAtom = atom<
	Array<{
		column: string;
		direction: SortDirection;
	}>
>([]);

// Pagination atoms
export const currentPageAtom = atom<number>(1);
export const itemsPerPageAtom = atom<number>(50);

// Selection atoms
export const selectedOrdersAtom = atom<Selection>(new Set<string>());
export const isAllMatchingOrdersSelectedAtom = atom<boolean>(false);

// Current orders list atom - tracks order IDs from current query for navigation
export const currentOrdersListAtom = atom<string[]>([]);

// Derived atom for API parameters
export const ordersParamsAtom = atom((get): SearchOrdersParams => {
	const searchValue = get(debouncedSearchValueAtom);
	const orderTab = get(orderTabFilterAtom);
	const actionRequired = get(actionRequiredFilterAtom);
	const currentPage = get(currentPageAtom);
	const itemsPerPage = get(itemsPerPageAtom);

	const params: SearchOrdersParams = {
		limit: itemsPerPage,
		page: currentPage - 1, // Convert to 0-indexed for API
		facets: ['*'], // Request all facets that match the query
	};

	if (searchValue.trim()) {
		params.query = searchValue.trim();
	}

	// Build filters object
	const filters: SearchOrdersParams['filters'] = {};

	// Add order tab filter
	if (orderTab) {
		filters.tabs = [orderTab];
	}

	// Add action required filter if enabled
	if (actionRequired) {
		filters.actionRequired = true;
	}

	// Add payment choice filters
	const paymentChoices = get(paymentChoiceFilterAtom);
	if (paymentChoices.length > 0) {
		filters.paymentChoice = paymentChoices;
	}

	// Add payment status filters
	const paymentStatuses = get(paymentStatusFilterAtom);
	if (paymentStatuses.length > 0) {
		filters.paymentStatus = paymentStatuses.length === 1 ? paymentStatuses[0] : paymentStatuses;
	}

	// Add financing status filters
	const financingStatuses = get(financingStatusFilterAtom);
	if (financingStatuses.length > 0) {
		filters.financingStatus =
			financingStatuses.length === 1 ? financingStatuses[0] : financingStatuses;
	}

	// Add model filters
	const models = get(modelFilterAtom);
	if (models.length > 0) {
		filters.name = models.length === 1 ? models[0] : models;
	}

	// Add type filters
	const types = get(typeFilterAtom);
	if (types.length > 0) {
		filters.vehicleType = types.length === 1 ? types[0] : types;
	}

	// Add registration state filter (single selection)
	const registrationState = get(registrationStateFilterAtom);
	if (registrationState) {
		filters.registrationState = registrationState;
	}

	// Add order date filter (single selection or custom range)
	const commonDateFilter = get(orderDateFilterAtom);
	if (commonDateFilter) {
		if (commonDateFilter === CommonDateFilter.CUSTOM) {
			const customRange = get(orderDateCustomRangeAtom);
			if (customRange.from !== null) {
				filters.orderDateRange = {
					gte: customRange.from,
					...(customRange.to !== null ? { lte: getEndOfDay(customRange.to) } : {}),
				};
			}
		} else {
			const dateRange = getCommonDateRange(commonDateFilter);
			if (dateRange) {
				filters.orderDateRange = dateRange;
			}
		}
	}

	// Add fulfillment filters (type + date range)
	const fulfillmentType = get(fulfillmentTypeFilterAtom);
	const fulfillmentDateFilter = get(fulfillmentDateFilterAtom);
	if (fulfillmentType) {
		filters.fulfillmentType = fulfillmentType;
		if (fulfillmentDateFilter) {
			if (fulfillmentDateFilter === DeliveryDateFilter.CUSTOM) {
				const customRange = get(fulfillmentDateCustomRangeAtom);
				if (customRange.from !== null) {
					filters.fulfillmentDateRange = {
						gte: customRange.from,
						...(customRange.to !== null ? { lte: getEndOfDay(customRange.to) } : {}),
					};
				}
			} else {
				const dateRange = getDeliveryDateRange(fulfillmentDateFilter);
				if (dateRange) {
					filters.fulfillmentDateRange = dateRange;
				}
			}
		}
	}

	if (Object.keys(filters).length > 0) {
		params.filters = filters;
	}

	// Add sorting if configured
	const sortConfig = get(sortConfigAtom);
	if (sortConfig.length > 0) {
		const sort = sortConfig[0]; // We only support single column sorting
		if (sort) {
			const sortBy = mapColumnToOrdersServiceSort(sort.column, sort.direction);
			if (sortBy) {
				params.sortBy = sortBy;
			}
		}
	}

	return params;
});

// Derived atom for base API parameters (without payment/financing/actionRequired filters)
// Used for getting facet counts that show total available options
// Only includes search query and order tab to provide context, but excludes all other filters
// so counts remain constant regardless of filter selections
export const ordersBaseParamsAtom = atom((get): SearchOrdersParams => {
	const searchValue = get(debouncedSearchValueAtom);
	const orderTab = get(orderTabFilterAtom);

	const params: SearchOrdersParams = {
		limit: 0, // We only need facets, not data
		page: 0,
		facets: ['*'], // Request all facets that match the query
	};

	if (searchValue.trim()) {
		params.query = searchValue.trim();
	}

	// Build filters object (exclude payment/financing/actionRequired filters)
	const filters: SearchOrdersParams['filters'] = {};

	// Add order tab filter
	if (orderTab) {
		filters.tabs = [orderTab];
	}

	// Note: We intentionally exclude:
	// - actionRequired: so counts don't change when toggling action required
	// - paymentChoice, paymentStatus, financingStatus: so counts show total available options
	// - name, vehicleType: so counts show total available options
	// - registrationState: so counts show total available options
	// - orderDate, deliveryDate, pickupDate: so counts show total available options

	if (Object.keys(filters).length > 0) {
		params.filters = filters;
	}

	return params;
});

// Action atom to handle search value changes
export const updateSearchValueAtom = atom(
	(get) => get(searchValueAtom),
	(get, set, newValue: string) => {
		set(debouncedSearchValueAtom, newValue);
		// Reset to first page when searching
		set(currentPageAtom, 1);
	},
);

// Action atom to handle order tab filter changes
export const updateOrdersTableTabFilterAtom = atom(
	(get) => get(orderTabFilterAtom),
	(get, set, newTab: OrdersTableTab | null) => {
		set(orderTabFilterAtom, newTab);
		// Clear selection when changing tab filter
		set(selectedOrdersAtom, new Set<string>());
		set(isAllMatchingOrdersSelectedAtom, false);
		// Reset to first page when changing tab filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle items per page changes
export const updateItemsPerPageAtom = atom(
	(get) => get(itemsPerPageAtom),
	(get, set, newItemsPerPage: number) => {
		set(itemsPerPageAtom, newItemsPerPage);
		// Reset to first page when changing items per page
		set(currentPageAtom, 1);
	},
);

// Action atom to handle sort changes
export const updateSortAtom = atom(
	null,
	(get, set, payload: { columnId: string; direction: SortDirection }) => {
		const { columnId, direction } = payload;
		if (direction === 'none') {
			set(sortConfigAtom, []);
		} else {
			set(sortConfigAtom, [{ column: columnId, direction }]);
		}
		// Reset to first page when sorting changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle action required filter changes
export const updateActionRequiredFilterAtom = atom(
	(get) => get(actionRequiredFilterAtom),
	(get, set, newValue: boolean) => {
		set(actionRequiredFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle payment choice filter changes
export const updatePaymentChoiceFilterAtom = atom(
	(get) => get(paymentChoiceFilterAtom),
	(get, set, newValue: string[]) => {
		set(paymentChoiceFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle payment status filter changes
export const updatePaymentStatusFilterAtom = atom(
	(get) => get(paymentStatusFilterAtom),
	(get, set, newValue: string[]) => {
		set(paymentStatusFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle financing status filter changes
export const updateFinancingStatusFilterAtom = atom(
	(get) => get(financingStatusFilterAtom),
	(get, set, newValue: string[]) => {
		set(financingStatusFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle model filter changes
export const updateModelFilterAtom = atom(
	(get) => get(modelFilterAtom),
	(get, set, newValue: string[]) => {
		set(modelFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle type filter changes
export const updateTypeFilterAtom = atom(
	(get) => get(typeFilterAtom),
	(get, set, newValue: string[]) => {
		set(typeFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle registration state filter changes
export const updateRegistrationStateFilterAtom = atom(
	(get) => get(registrationStateFilterAtom),
	(get, set, newValue: string | null) => {
		set(registrationStateFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle order date filter changes
export const updateOrderDateFilterAtom = atom(
	(get) => get(orderDateFilterAtom),
	(get, set, newValue: CommonDateFilter | null) => {
		set(orderDateFilterAtom, newValue);
		// Clear custom range when switching away from custom
		if (newValue !== CommonDateFilter.CUSTOM) {
			set(orderDateCustomRangeAtom, { from: null, to: null });
		}
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle order date custom range changes
export const updateOrderDateCustomRangeAtom = atom(
	(get) => get(orderDateCustomRangeAtom),
	(get, set, newValue: { from: number | null; to: number | null }) => {
		set(orderDateCustomRangeAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle fulfillment type filter changes
export const updateFulfillmentTypeFilterAtom = atom(
	(get) => get(fulfillmentTypeFilterAtom),
	(get, set, newValue: FulfillmentType | null) => {
		set(fulfillmentTypeFilterAtom, newValue);
		// Clear date filter and custom range when type changes
		if (newValue === null) {
			set(fulfillmentDateFilterAtom, null);
			set(fulfillmentDateCustomRangeAtom, { from: null, to: null });
		}
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle fulfillment date filter changes
export const updateFulfillmentDateFilterAtom = atom(
	(get) => get(fulfillmentDateFilterAtom),
	(get, set, newValue: DeliveryDateFilter | null) => {
		set(fulfillmentDateFilterAtom, newValue);
		// Clear custom range when switching away from custom
		if (newValue !== DeliveryDateFilter.CUSTOM) {
			set(fulfillmentDateCustomRangeAtom, { from: null, to: null });
		}
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle fulfillment date custom range changes
export const updateFulfillmentDateCustomRangeAtom = atom(
	(get) => get(fulfillmentDateCustomRangeAtom),
	(get, set, newValue: { from: number | null; to: number | null }) => {
		set(fulfillmentDateCustomRangeAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle selected orders changes — resets "all matching" when selection changes away from 'all'
export const updateSelectedOrdersAtom = atom(
	(get) => get(selectedOrdersAtom),
	(get, set, newSelection: Selection) => {
		set(selectedOrdersAtom, newSelection);
		if (newSelection !== 'all') {
			set(isAllMatchingOrdersSelectedAtom, false);
		}
	},
);

// Action atom to update current orders list
export const updateCurrentOrdersListAtom = atom(
	(get) => get(currentOrdersListAtom),
	(get, set, newList: string[]) => {
		set(currentOrdersListAtom, newList);
	},
);

// Action atom to clear all filters (excluding orders table tab, which is controlled by tabs)
export const clearAllFiltersAtom = atom(null, (get, set) => {
	set(debouncedSearchValueAtom, '');
	set(actionRequiredFilterAtom, false);
	set(paymentChoiceFilterAtom, []);
	set(paymentStatusFilterAtom, []);
	set(financingStatusFilterAtom, []);
	set(modelFilterAtom, []);
	set(typeFilterAtom, []);
	set(registrationStateFilterAtom, null);
	set(orderDateFilterAtom, null);
	set(orderDateCustomRangeAtom, { from: null, to: null });
	set(fulfillmentTypeFilterAtom, null);
	set(fulfillmentDateFilterAtom, null);
	set(fulfillmentDateCustomRangeAtom, { from: null, to: null });
	// Reset to first page when clearing filters
	set(currentPageAtom, 1);
});
